import { get, isEmpty } from 'lodash';
import {
  activityCategoryFragment,
  activityFragment,
  rentalsFragment,
  thematicFragment,
} from 'Client/cms/toolkit/product-utils/productFeatures.queries';
import BookingAttendeesProfile from 'Components/widgets/contact-info/BookingAttendeesProfile.fragment';
import { profileFragment } from 'Components/widgets/contact-info/ContactInfo.fragment';
import type { StreetViewsByAccommodationCategoryIdQueryParam } from 'Store/streetViews/streetViews.action';
import validationSchemaFieldFragment from 'Store/validation-schema/validationSchemaField.fragment';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  getPriceDetailFragment,
  proposalFields,
  transportDetailsFragment,
  paymentProvidersRequest,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  PROPOSAL_GET_BY_ID,
  STREET_VIEW_GET_SEVERAL,
  PROPOSAL_GET_SCHEMA,
} from 'Utils/request/requestDictionary';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
export type ProposalsByIdQueryParam = {
  adultsAttendeesIds?: Array<string>;
  fetchActivityCategories?: boolean;
  isJoyView?: boolean;
  isUrbanOasis?: boolean;
  kidsAttendeesIds?: Array<string>;
  productId?: String;
  proposalsId: ReadonlyArray<string> | string;
  shouldRequestPayment: boolean;
  selectedProposalId?: string;
};
const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';
export function getResort(fetchActivityCategories: boolean = false) {
  return `
    resort {
      accommodations {
        area {
          max
          min
          unit
        }
        bathroomEquipments {
          id
          label
        }
        category
        categoryId
        comfortId
        comfortType
        connectingRoom
        description
        equipments {
          id
          label
          ranking
        }
        exclusiveCollection
        floorPlans
        id
        label
        maxCapacity
        media {
          photos {
            url
          }
        }
        minCapacity
        outsideFacilities {
          id
          label
        }
        ranking
        roomType
        services {
          id
          label
          ranking
        }
        strengths {
          id
          label
        }
        surface
        view {
          id
          label
        }
      }
      thematicActivityTree {
        ...thematics
        activities {
          ...activity
        }
      }
      ${
        fetchActivityCategories
          ? `activityTree {
        ...activityCategory
        activities {
          ...activity
        }
        categories {
          ...activityCategory
          activities {
            ...activity
          }
          categories {
            ...activityCategory
            activities {
              ...activity
            }
          }
        }
      }`
          : ''
      }
      adultOnly
      associatedResort{
        id
        name
      }
      badgeIcon
      category
      childcareDetails {
        isIncluded: included
        ageInMonths {
          min
          max
        }
      }
      childrenActivities: productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
        ageInMonths {
          max
          min
        }
        description
        equipments {
          id
          label
        }
        id
        isIncluded
        label
        subtitle
      }
      color
      comfort
      comfortLabel
      destination {
        area
        country
      }
      facilities(types: ["CHILDREN","PARKING"], withEquipments: true) {
        equipments {
          id
          label
        }
        label
        subtitle
        type
      }
      factSheetUrl
      href
      id
      image
      images
      hasECSpace
      isCruise
      isExclusiveCollection
      isExplicitEC
      isJoyview
      isUrbanOasis
      isWinter
      name
      offsetSell
      openingDates {
        opening
        closing
      }
      ${
        fetchActivityCategories
          ? `rentalsTree {
        ...rentals
      }`
          : ''
      }
      seasonName
      seasons
      title
      transportType
      type
      ${
        fetchActivityCategories
          ? `activityCategories {
        leisure {
          id
          label
          subCategories {
            id
            label
            imageUrl
            keywords
            startDate
            endDate
            activities {
              id
              label
              description
              isIncluded
              durationDescription
              practiceLevelDescription
              activityTags{
                id
                label
              }
              ageInMonths {
                min
                max
              }
            }
          }
        }
        wellness {
          id
          label
          subCategories {
            id
            label
            imageUrl
            keywords
            activities {
              id
              label
              description
              isIncluded
              durationDescription
              practiceLevelDescription
              activityTags{
                id
                label
              }
              ageInMonths {
                min
                max
              }
            }
          }
        }
      }`
          : ''
      }
    }`;
}

function getValidationSchema(
  proposalIds?: ReadonlyArray<string> | string,
  resource: 'billing' | 'profile',
  locale?: string,
): string {
  const proposalId = Array.isArray(proposalIds) ? proposalIds[0] : proposalIds;
  return `
    {
      validationSchema(
        proposalId: "${proposalId || ''}",
        resource : "${resource}",
        fields: ["address"],
        locale: "${locale || ''}") {
        ...ValidationSchemaField
      }
    }
    ${validationSchemaFieldFragment}
    `;
}

export async function fetchValidationSchema(
  proposalId?: ReadonlyArray<string> | string,
  appContext: AppContext,
  resource: 'billing' | 'profile',
): Promise<any> {
  return graphQLClient.runNoCacheQuery(getValidationSchema(proposalId, resource), appContext, {
    requestName: PROPOSAL_GET_SCHEMA,
  });
}

const getAttendeesData = ({
  adultsAttendeesIds,
  kidsAttendeesIds,
}: {
  adultsAttendeesIds: ProposalsByIdQueryParam['adultsAttendeesIds'];
  kidsAttendeesIds: ProposalsByIdQueryParam['kidsAttendeesIds'];
}) => `${
  adultsAttendeesIds && adultsAttendeesIds[0]
    ? `adultsAttendees: attendees(attendeesIds: ["${adultsAttendeesIds.join('","')}"]) {
          ...BookingAttendeesProfile
          birthdate: dateOfBirth
          email
          mobile: mobilePhoneNumber
          optinEmail
          optinSms
          optinPhone
          optinMail
          optinClubmedPlus
          identityNumber
          healthPassId
          address {
            streetNumber
            streetName
            additionalInformation1
            additionalInformation2
            addOn
            zipCode
            city
            town
            stateOrDistrict
            countryCode
            country
          }
          document {
            passport {
              documentNumber
              issuingCountry
              expirationDate
              issuingDate
              birthCountry
              birthCity
              secondName
              thirdName
              fourthName
              type
            }
            idcard {
              documentNumber
              issuingCountry
              expirationDate
              issuingDate
              birthCountry
              birthCity
              secondName
              thirdName
              fourthName
              type
            }
          }
        }`
    : ''
}
  ${
    // no need of birthday, we already have it
    kidsAttendeesIds && kidsAttendeesIds[0]
      ? `kidsAttendees: attendees(attendeesIds: ["${kidsAttendeesIds.join('","')}"]) {
          ...BookingAttendeesProfile
          healthPassId
          document {
            passport {
              documentNumber
              issuingCountry
              expirationDate
              issuingDate
            }
            idcard {
              documentNumber
              issuingCountry
              expirationDate
              issuingDate
            }
          }
        }`
      : ''
  }
`;

/*
 * * Used in fetchProposalsById to get transports list and node for <TransportsSection/>
 * * Uses  API route /v2/proposals/{proposal_id}
 */
async function proposalsByIdQuery(
  params: ProposalsByIdQueryParam,
  appContext: AppContext,
): Promise<any> {
  const proposalsArgs = [
    `ids: ["${Array.isArray(params.proposalsId) ? params.proposalsId.join('","') : params.proposalsId}"]`,
  ];
  const displayCommission = isFeatureFlipped(appContext)('booking.displayCommission');
  const proposalsResponse = await graphQLClient.runNoCacheQuery(
    `
      {
        proposals: proposalsById(${proposalsArgs.join(', ')}) {
          ... on ProposalsType {
            proposals {
              ${proposalFields(
                {
                  isJoyView: true,
                  isUrbanOasis: true,
                  withCommission:
                    (appContext.isCmta || appContext.isIndirectCxo) && displayCommission,
                  withSchedule: params.shouldRequestPayment,
                },
                appContext,
              )}
            }
            ${getResort(params.fetchActivityCategories)}
          }
        }
        ${appContext.isUserLogged ? 'profile {...BookingProfile}' : ''}
        ${params.shouldRequestPayment ? paymentProvidersRequest() : ''}
      }
      ${transportDetailsFragment}
      ${getPriceDetailFragment(get(appContext, 'locale'))}
      ${appContext.isUserLogged ? profileFragment : ''}
      ${params.fetchActivityCategories ? activityCategoryFragment : ''}
      ${params.fetchActivityCategories || thematicFragment ? activityFragment : ''}
      ${params.fetchActivityCategories ? rentalsFragment : ''}
      ${thematicFragment}
    `,
    appContext,
    {
      requestName: PROPOSAL_GET_BY_ID,
    },
  );
  const proposals = proposalsResponse.proposals?.proposals || [];
  const selectedProposal =
    (params.selectedProposalId && proposals.find(({ id }) => id === params.selectedProposalId)) ||
    proposals[0];

  if (!selectedProposal) {
    return { ...proposalsResponse };
  }

  const adultsAttendeesIds = params?.adultsAttendeesIds?.length
    ? params.adultsAttendeesIds
    : (selectedProposal?.attendees.adults || [])
        .map(({ customerId }) => customerId)
        .filter(Boolean);
  const kidsAttendeesIds = params?.kidsAttendeesIds?.length
    ? params.kidsAttendeesIds
    : (selectedProposal?.attendees.children || [])
        .map(({ customerId }) => customerId)
        .filter(Boolean);
  const hasAttendeeIds = !isEmpty(adultsAttendeesIds) || !isEmpty(kidsAttendeesIds);
  const validationSchema = await fetchValidationSchema(selectedProposal.id, appContext, 'profile');

  if (!appContext.isSeller || !hasAttendeeIds) {
    return {
      selectedProposal,
      ...proposalsResponse,
      ...validationSchema,
    };
  }

  const attendeesResponse = await graphQLClient.runNoCacheQuery(
    `
      {
        ${
          // When it is an attendee identified on the Cxo side we already know the customerIds
          // via the cxo_traveler query param
          getAttendeesData({
            adultsAttendeesIds,
            kidsAttendeesIds,
          })
        }
      }
      ${appContext.isUserLogged ? 'profile {...BookingProfile}' : ''}
      ${(adultsAttendeesIds && adultsAttendeesIds[0]) || (kidsAttendeesIds && kidsAttendeesIds[0]) ? BookingAttendeesProfile : ''}
    `,
    appContext,
    {
      requestName: PROPOSAL_GET_BY_ID,
    },
  );
  return {
    selectedProposal,
    ...proposalsResponse,
    ...attendeesResponse,
    ...validationSchema,
  };
}

/*
 * * Used in fetchStreetViewsByAccommodationCategoryId to get street views list for <AccommodationRoomSlideshow/>
 * * Uses  API route GET /v0/products/{product_id}/accommodation_categories/{accommodation_category_id}/virtual_visits
 */
function streetViewsByAccommodationCategoryId(
  params: StreetViewsByAccommodationCategoryIdQueryParam,
  appContext: AppContext,
): Promise<any> {
  const streetViewsArgs = [`productId: "${params.productId}"`];
  streetViewsArgs.push(
    Array.isArray(params.accommodationCategoryIds)
      ? `accommodationCategoryIds: ["${params.accommodationCategoryIds.join('","')}"]`
      : `accommodationCategoryIds: ["${params.accommodationCategoryIds}"]`,
  );
  return graphQLClient.runNoCacheQuery(
    `
    {
      streetViews(${streetViewsArgs.join(', ')}) {
        accommodationCategoryId
        streetView {
          latitude
          longitude
          heading
          pitch
          fov
          url
        }
      }
    }
  `,
    appContext,
    {
      requestName: STREET_VIEW_GET_SEVERAL,
    },
  );
}

export {
  getAttendeesData,
  proposalsByIdQuery,
  streetViewsByAccommodationCategoryId,
  getValidationSchema,
};
